import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as claimApi from "./api";
import type {
  ClaimCreateRequest,
  ClaimDecisionRequest,
  ClaimFilters,
  ClaimLineCreateRequest,
  ClaimLineUpdateRequest,
  ClaimUpdateRequest,
} from "./types";

export function useClaimsStats() {
  return useQuery({
    queryKey: ["claims", "stats"],
    queryFn: claimApi.fetchClaimsStats,
    retry: false,
    staleTime: 60 * 1000, // 1 min — stats don't need to be real-time
  });
}

export function useCurrentEmployee() {
  return useQuery({
    queryKey: ["employees", "me"],
    queryFn: claimApi.fetchCurrentEmployee,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

export function useMyClaims(filters?: ClaimFilters) {
  return useQuery({
    queryKey: ["claims", "my", filters],
    queryFn: () => claimApi.fetchMyClaims(filters),
    enabled: filters !== undefined,
    retry: false,
    placeholderData: (previous) => previous,
  });
}

export function useClaimsForApproval(filters?: ClaimFilters) {
  return useQuery({
    queryKey: ["claims", "approvals", filters],
    queryFn: () => claimApi.fetchClaimsForApproval(filters),
    retry: false,
    placeholderData: (previous) => previous,
  });
}

export function useAllClaims(filters?: ClaimFilters) {
  return useQuery({
    queryKey: ["claims", "all", filters],
    queryFn: () => claimApi.fetchAllClaims(filters),
    enabled: filters !== undefined,
    retry: false,
    placeholderData: (previous) => previous,
  });
}

export function useClaim(id?: number) {
  return useQuery({
    queryKey: ["claims", "detail", id],
    queryFn: () => claimApi.fetchClaimById(id as number),
    enabled: Number.isFinite(id) && Number(id) > 0,
    retry: false,
  });
}

export function useCreateClaim() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: ClaimCreateRequest) => claimApi.createClaim(payload),
    onSuccess: (claim) => {
      qc.invalidateQueries({ queryKey: ["claims"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["claims", "detail", claim.id] });
    },
  });
}

export function useUpdateClaim(id: number) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: ClaimUpdateRequest) =>
      claimApi.updateClaim(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claims"] });
      qc.invalidateQueries({ queryKey: ["claims", "detail", id] });
    },
  });
}

export function useAddClaimLine(claimId: number) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: ClaimLineCreateRequest) =>
      claimApi.addClaimLine(claimId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claims"] });
      qc.invalidateQueries({ queryKey: ["claims", "detail", claimId] });
    },
  });
}

export function useUpdateClaimLine(claimId: number) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      lineId,
      payload,
    }: {
      lineId: number;
      payload: ClaimLineUpdateRequest;
    }) => claimApi.updateClaimLine(claimId, lineId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claims"] });
      qc.invalidateQueries({ queryKey: ["claims", "detail", claimId] });
    },
  });
}

export function useDeleteClaimLine(claimId: number) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (lineId: number) => claimApi.deleteClaimLine(claimId, lineId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claims"] });
      qc.invalidateQueries({ queryKey: ["claims", "detail", claimId] });
    },
  });
}

export function useSubmitClaim() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => claimApi.submitClaim(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["claims"] });
      qc.invalidateQueries({ queryKey: ["claims", "detail", id] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useApproveClaim() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: ClaimDecisionRequest;
    }) => claimApi.approveClaim(id, payload),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ["claims"] });
      qc.invalidateQueries({ queryKey: ["claims", "detail", variables.id] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useRejectClaim() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: ClaimDecisionRequest;
    }) => claimApi.rejectClaim(id, payload),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ["claims"] });
      qc.invalidateQueries({ queryKey: ["claims", "detail", variables.id] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useMarkClaimPaid() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => claimApi.markClaimPaid(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["claims"] });
      qc.invalidateQueries({ queryKey: ["claims", "detail", id] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useCancelClaim() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, comments }: { id: number; comments?: string }) =>
      claimApi.cancelClaim(id, comments),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ["claims"] });
      qc.invalidateQueries({ queryKey: ["claims", "detail", variables.id] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useUploadClaimDocument(claimId: number) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      documentType,
      lineId,
    }: {
      file: File;
      documentType?: string;
      lineId?: number;
    }) => claimApi.uploadClaimDocument(claimId, file, documentType, lineId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claims"] });
      qc.invalidateQueries({ queryKey: ["claims", "detail", claimId] });
    },
  });
}

export function useDeleteClaimDocument(claimId: number) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (documentId: number) =>
      claimApi.deleteClaimDocument(claimId, documentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claims"] });
      qc.invalidateQueries({ queryKey: ["claims", "detail", claimId] });
    },
  });
}